var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useEffect, useState } from 'react';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import './booker.css';
import 'moment/locale/es-mx';
import { HotelForm } from '../HotelForm/index.js';
import { HotelFlightForm } from '../HotelFlightForm/index.js';
import classNames from 'classnames';
import { LoadingForm } from '../LoadingForm/index.js';
import ServiceSelector from '../Services/ServiceSelector.js';
import { desaturate, getRGBColor, getShadeColor } from 'utils/layout';
export const BookerContext = createContext({});
export const Booker = (_a) => {
    var _b;
    var { children, className, useTailwindPrefix, styleOptions = {
        bookerBgColor: '#ffffff',
        bookerA11yColor: '#565a5c',
        bookerBorderWidth: 0,
        bookerPaddingVertical: '1rem',
        bookerPaddingHorizontal: '1rem',
        bookerRadius: '0.5rem',
        bookerOverlaysRadius: '0.5rem',
    }, version = 'regular', saveCSSVarColorsWithotPrefix } = _a, rest = __rest(_a, ["children", "className", "useTailwindPrefix", "styleOptions", "version", "saveCSSVarColorsWithotPrefix"]);
    const [isMobile, setIsMobile] = useState(false);
    const [numberOfMonths, setNumberOfMonths] = useState(2);
    const [enableOutsideDays, setEnableOutsideDays] = useState(false);
    const onWindowResize = useCallback(() => {
        const frameWidth = window.innerWidth;
        if (frameWidth <= 1200) {
            setNumberOfMonths(1);
            setEnableOutsideDays(true);
            if (frameWidth < 768) {
                setNumberOfMonths(2);
                setEnableOutsideDays(false);
                setIsMobile(true);
            }
            else {
                setIsMobile(false);
            }
        }
        else {
            setNumberOfMonths(2);
            setIsMobile(false);
            setEnableOutsideDays(false);
        }
    }, []);
    useEffect(() => {
        onWindowResize();
        window.addEventListener('resize', onWindowResize);
        return () => {
            window.removeEventListener('resize', onWindowResize);
        };
    }, [onWindowResize]);
    function mod_getRGBColor(hex, name) {
        if (saveCSSVarColorsWithotPrefix) {
            return getRGBColor(hex, name, '');
        }
        return getRGBColor(hex, name);
    }
    const getStyleOptions = (primaryColor = '#417505') => {
        const values = [];
        values.push(getRGBColor(getShadeColor(primaryColor, -0.1), 'calendarDay-selectedBg', 'drs-color'));
        values.push(getRGBColor(getShadeColor(primaryColor, -0.3), 'calendarDay-selectedBorder', 'drs-color'));
        values.push(getRGBColor(primaryColor, 'calendarDay-spanBg', 'drs-color'));
        values.push(getRGBColor(getShadeColor(primaryColor, -0.09), 'calendarDay-spanBorder', 'drs-color'));
        values.push(getRGBColor(getShadeColor(desaturate(primaryColor, 0.7), 0.6), 'calendarDay-spanHoverBg', 'drs-color'));
        values.push(getRGBColor(getShadeColor(desaturate(primaryColor, 0.7), 0.4), 'calendarDay-spanHoverBorder', 'drs-color'));
        values.push(getRGBColor(getShadeColor(primaryColor, -0.2), 'calendarDay-spanHoverColor', 'drs-color'));
        if (version !== 'padded') {
            return values;
        }
        if (styleOptions === null || styleOptions === void 0 ? void 0 : styleOptions.bookerPaddingVertical) {
            values.push(`--widget-pill-padding-vertical: ${styleOptions.bookerPaddingVertical}${typeof styleOptions.bookerPaddingVertical === 'number' ? 'px' : ''};`);
        }
        if (styleOptions === null || styleOptions === void 0 ? void 0 : styleOptions.bookerPaddingHorizontal) {
            values.push(`--widget-pill-padding-horizontal: ${styleOptions.bookerPaddingHorizontal}${typeof styleOptions.bookerPaddingHorizontal === 'number' ? 'px' : ''};`);
        }
        if (styleOptions === null || styleOptions === void 0 ? void 0 : styleOptions.bookerRadius) {
            values.push(`--widget-pill-border-radius: ${styleOptions.bookerRadius}${typeof styleOptions.bookerRadius === 'number' ? 'px' : ''};`);
        }
        if (styleOptions === null || styleOptions === void 0 ? void 0 : styleOptions.bookerBgColor) {
            values.push(mod_getRGBColor(styleOptions.bookerBgColor, 'widget-pill-background'));
            values.push(`--widget-pill-background-opacity: ${styleOptions.bookerBgOpacity !== null &&
                styleOptions.bookerBgOpacity !== undefined &&
                styleOptions.bookerBgOpacity >= 0 &&
                styleOptions.bookerBgOpacity <= 1
                ? styleOptions.bookerBgOpacity
                : 1};`);
        }
        if (styleOptions === null || styleOptions === void 0 ? void 0 : styleOptions.bookerA11yColor) {
            //se agrega una para ser usada por drs y otra por tailwind
            values.push(getRGBColor(styleOptions.bookerA11yColor, 'widget-pill-a11y', 'drs-color'));
            values.push(mod_getRGBColor(styleOptions.bookerA11yColor, 'widget-pill-a11y'));
        }
        if ((styleOptions === null || styleOptions === void 0 ? void 0 : styleOptions.bookerBorderWidth) !== null &&
            (styleOptions === null || styleOptions === void 0 ? void 0 : styleOptions.bookerBorderWidth) !== undefined &&
            styleOptions.bookerBorderWidth >= 0) {
            values.push(`--widget-pill-border-width: ${styleOptions.bookerBorderWidth}px;`);
            if (styleOptions === null || styleOptions === void 0 ? void 0 : styleOptions.bookerBorderColor) {
                values.push(mod_getRGBColor(styleOptions.bookerBorderColor, 'widget-pill-border'));
            }
        }
        if (styleOptions === null || styleOptions === void 0 ? void 0 : styleOptions.bookerOverlaysRadius) {
            values.push(`--widget-pill-overlays-border-radius: ${styleOptions.bookerOverlaysRadius}${typeof styleOptions.bookerOverlaysRadius === 'number' ? 'px' : ''};`);
        }
        return values;
    };
    return (_jsxs(BookerContext.Provider, Object.assign({ value: Object.assign({ isMobile, numberOfMonths, useTailwindPrefix, enableOutsideDays, version }, rest) }, { children: [_jsx("style", { children: `:root {
               ${(_b = getStyleOptions(rest.primaryColor)) === null || _b === void 0 ? void 0 : _b.join(' ')}
            }` }), _jsx("div", Object.assign({ className: classNames(className, {
                    'itm-booker--vertical': rest.vertical,
                    'itm-booker--horizontal': !rest.vertical,
                    'text-[#565a5c] text-[16px]': !className && !useTailwindPrefix,
                    'tw-text-[#565a5c] tw-text-[16px]': !className && useTailwindPrefix,
                    'mb-4': rest.inline && !useTailwindPrefix,
                    'tw-mb-4': rest.inline && useTailwindPrefix,
                }) }, { children: children }))] })));
};
Booker.Hotel = HotelForm;
Booker.HotelFlight = HotelFlightForm;
Booker.Services = ServiceSelector;
Booker.Loading = LoadingForm;
